import axios from "axios";
import { some } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import FilterForm from "./FilterForm";

import {
    handleChange as handleChangeAction,
    handleChangeToInitState as handleChangeToInitStateAction,
} from "../../../actions/handleChangeAction";
import { getIleggelseleveringstyper as getIleggelseleveringstyperAction } from "../../../actions/ileggelseleveringstyperActions";
import { getIleggelser as getIleggelserAction } from "../../../actions/ileggelserActions";
import { getIleggelsestyper as getIleggelsestyperAction } from "../../../actions/ileggelsestyperActions";
import { getKlageKoder } from "../../../actions/klagekoderAction";
import { getVedtakskoder } from "../../../actions/vedtakskoderAction";

import {
    getDropDownListSearchVedtakskoder,
    getSelectOptionKlagekoder,
} from "../../../selectors/ileggelserSelector";
import { getSelectOptionsEnhanced } from "../../../selectors/selectOptionsSelector";

import { ileggelseStatuser } from "../../../constants/globals";
import * as inputValidation from "../../../utils/inputValidationUtils";
import { IsValidated } from "../../../utils/validationUtils";

import { setPage as setPageAction } from "../../../actions/advancedListActions";
import { replaceWhitespacesWithEmptyStrings } from "../../../utils/sanitizationUtils";
import "./IleggelserOversiktFilterContainer.css";

import { stedfortrederDriftsleder } from "../../../constants/roles";
import { authorized } from "../../../utils/authorizationUtils";

const FORM_KEY = "ILEGGELSER_OVERSIKT_FILTER_FORM";

const rules = {
  ileggelsesnummer: "isPositiveInteger",
  tjenestenummer: "isPositiveInteger",
};

export default function FilterContainer() {
  const dispatch = useDispatch();
  const ileggelserOversiktFilterForm = useSelector(
    (state) => state.forms.ileggelserOversiktFilterForm,
  );
  const overtredelseskoderIleggelser = useSelector(
    (state) => state.overtredelseskoderIleggelser,
  );
  const ileggelsestyperAll = useSelector((state) =>
    getSelectOptionsEnhanced(state, "ileggelsestyper", "navn", "id"),
  );
  const ileggelsestyper = useMemo(
    () => ileggelsestyperAll ? ileggelsestyperAll.filter((t) => t.navn !== "Miljøgebyr") : [],
    [ileggelsestyperAll],
  );
  const ileggelseleveringstyper = useSelector((state) =>
    getSelectOptionsEnhanced(state, "ileggelseleveringstyper", "navn", "id"),
  );
  const betjentgrupper = useSelector((state) =>
    getSelectOptionsEnhanced(state, "betjentgrupper", "navn", "id", {
      text: "Velg",
      value: "",
    }),
  );
  const vedtakskoder = useSelector(getDropDownListSearchVedtakskoder);
  const klagekoder = useSelector(getSelectOptionKlagekoder);
  const ileggelser = useSelector((state) => state.ileggelser);

  const [showSokSpinner, setShowSokSpinner] = useState(false);
  const axiosCancelTokenSource = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    dispatch(getIleggelsestyperAction());
    dispatch(getIleggelseleveringstyperAction());
    if (!authorized([stedfortrederDriftsleder])) {
      dispatch(getVedtakskoder());
      dispatch(getKlageKoder());
    }
    return () => {
      axiosCancelTokenSource.current && axiosCancelTokenSource.current.cancel();
    };
  }, []);

  const dispatchChange = (name, value) =>
    dispatch(handleChangeAction(FORM_KEY, name, value));

  const handleChange = (e) => {
    if (e.target.name === "selectedStatuser") {
      ileggelseStatuser.forEach((ileggelseStatus) => {
        if (some(e.target.value, ileggelseStatus)) {
          dispatchChange(ileggelseStatus.id, true);
        } else {
          dispatchChange(ileggelseStatus.id, null);
        }
      });
    }
    dispatchChange(e.target.name, e.target.value);
  };

  const handleBlur = (e) => {
    if (
      e.target.name === "ileggelsesnummer" ||
      e.target.name === "tjenestenummer" ||
      e.target.name === "saksnummer"
    ) {
      dispatchChange(
        e.target.name,
        replaceWhitespacesWithEmptyStrings(e.target.value),
      );
    }
  };

  const handleChangeToInitState = () => {
    axiosCancelTokenSource.current && axiosCancelTokenSource.current.cancel();
    dispatch(handleChangeToInitStateAction(FORM_KEY));
  };

  const isValid = () => {
    const propsAsFakeState = {
      ileggelsesnummer: ileggelserOversiktFilterForm.ileggelsesnummer,
      tjenestenummer: ileggelserOversiktFilterForm.tjenestenummer,
      saksnummer: ileggelserOversiktFilterForm.saksnummer,
      validationError: {
        ileggelsesnummer:
          ileggelserOversiktFilterForm.validationError.ileggelsesnummer,
        tjenestenummer:
          ileggelserOversiktFilterForm.validationError.tjenestenummer,
      },
    };
    const { isValid: formIsValid, validationError } = IsValidated(
      propsAsFakeState,
      rules,
    );
    dispatchChange(
      "validationError.ileggelsesnummer",
      validationError.ileggelsesnummer,
    );
    dispatchChange(
      "validationError.tjenestenummer",
      validationError.tjenestenummer,
    );

    const fraDateTimeTilDateTimeValid =
      inputValidation.fraDateTimeTilDateTimeValidation(
        ileggelserOversiktFilterForm,
        dispatchChange,
      );

    const fraOgTilIneOverforingsdatoValidationErrors =
      inputValidation.getDateTimeValidationError({
        fraDato: ileggelserOversiktFilterForm.fraIneOverforingsdato,
        tilDato: ileggelserOversiktFilterForm.tilIneOverforingsdato,
      });
    dispatchChange(
      "fraIneOverforingsdatoValidationErrorText",
      fraOgTilIneOverforingsdatoValidationErrors.fraDatoValidationErrorText,
    );
    dispatchChange(
      "tilIneOverforingsdatoValidationErrorText",
      fraOgTilIneOverforingsdatoValidationErrors.tilDatoValidationErrorText,
    );

    const fraOgTilOverforingsdatoIneValid = !(
      fraOgTilIneOverforingsdatoValidationErrors.fraDatoValidationErrorText ||
      fraOgTilIneOverforingsdatoValidationErrors.tilDatoValidationErrorText
    );

    return formIsValid && fraDateTimeTilDateTimeValid && fraOgTilOverforingsdatoIneValid;
  };

  const getIleggelserFilter = () => {
    let {
      fraTid,
      tilTid,
      fraDatoValidationErrorText,
      fraTidValidationErrorText,
      tilDatoValidationErrorText,
      tilTidValidationErrorText,
      status,
      ...filter
    } = ileggelserOversiktFilterForm;

    return {
      ...filter,
      fraDato: filter.fraDato && filter.fraDato.toISOString(),
      tilDato: filter.tilDato && filter.tilDato.toISOString(),
    };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isValid()) {
      setShowSokSpinner(true);
      const page = 1;
      axiosCancelTokenSource.current = axios.CancelToken.source();
      dispatch(
        getIleggelserAction(
          getIleggelserFilter(),
          page,
          ileggelser.count,
          axiosCancelTokenSource.current.token,
        ),
      )
        .then(() => {
          setShowSokSpinner(false);
          dispatch(setPageAction("ileggelser", page));
        })
        .catch(() => setShowSokSpinner(false));
    }
  };

  return (
    <div className="ileggelse-filter-container">
      <h1>Søk etter ileggelser</h1>
      <FilterForm
        showSokSpinner={showSokSpinner}
        ileggelseFilterFormState={ileggelserOversiktFilterForm}
        overtredelseskoderIleggelser={overtredelseskoderIleggelser}
        ileggelsestyper={ileggelsestyper}
        ileggelseleveringstyper={ileggelseleveringstyper}
        betjentgrupper={betjentgrupper}
        ileggelsesstatuser={ileggelseStatuser}
        klagekoder={klagekoder}
        vedtakskoder={vedtakskoder}
        handleChange={handleChange}
        handleBlur={handleBlur}
        handleChangeToInitState={handleChangeToInitState}
        handleSubmit={handleSubmit}
      />
    </div>
  );
}
